import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, ColumnDef } from '@tanstack/react-table'
import { SimpleTable } from 'components/tables'
import { LinkedStatus } from './LinkedStatus'
import { LinkedButton } from './LinkedButton'
import { TwoColumn } from 'components/layout'
import JiraLogo from '../assets/jira.png'
import FlowLogo from '../assets/flow.png'
import { ProjectIntegrationType } from 'types'

interface PMISectionProps {
  project: AdminProject
  integrationLinks: Array<{
    integration: ProjectIntegration
    link: IntegrationProjectLink | undefined
  }>
  onLink: (project: AdminProject, integration: ProjectIntegration) => void
  onUnlink: (project: AdminProject, link: IntegrationProjectLink) => void
}

interface PMIRow {
  integration: ProjectIntegration
  link: IntegrationProjectLink | undefined
}

const columnHelper = createColumnHelper<PMIRow>()

const getIntegrationLogo = (integrationType: ProjectIntegrationType) => {
  switch (integrationType) {
    case ProjectIntegrationType.Jira:
      return JiraLogo
    case ProjectIntegrationType.ShotGrid:
      return FlowLogo
    default:
      const _exhaustiveCheck: never = integrationType
      throw new Error(
        `Unsupported integration type: ${integrationType}. Please add a logo for this integration.`,
      )
  }
}

export const PMISection: FC<PMISectionProps> = ({
  project,
  integrationLinks,
  onLink,
  onUnlink,
}) => {
  const { t } = useTranslation()

  const columns = [
    columnHelper.accessor('integration.name', {
      id: 'integration',
      header: t('features.projectsAdmin.projectDetails.integration'),
      cell: ({ getValue, row }) => {
        const integrationName = getValue()
        const logo = getIntegrationLogo(row.original.integration.type)

        return (
          <span className="flex items-center gap-3">
            {logo && (
              <img
                src={logo}
                alt={t(
                  'features.projectsAdmin.projectDetails.integrationLogoAlt',
                  { integration: integrationName },
                )}
                className="w-5 h-5"
              />
            )}
            {integrationName}
          </span>
        )
      },
    }),
    columnHelper.accessor((row) => Boolean(row.link), {
      id: 'status',
      header: t('common.status'),
      cell: ({ getValue }) => <LinkedStatus linked={getValue()} />,
    }),
    columnHelper.accessor((row) => row.link?.sourceProject.name ?? t('common.notAvailable'), {
      id: 'projectName',
      header: t('features.projectsAdmin.projectName'),
    }),
    columnHelper.accessor((row) => row.link?.sourceProject.id ?? t('common.notAvailable'), {
      id: 'projectId',
      header: t('features.projectsAdmin.projectDetails.projectId'),
    }),
    columnHelper.accessor(
      (row) => ({ link: row.link, integration: row.integration }),
      {
        id: 'action',
        header: t('common.action'),
        cell: ({ getValue }) => {
          const { link, integration } = getValue()
          const isLinked = Boolean(link)
          return (
            <LinkedButton
              linked={isLinked}
              onLink={() => onLink(project, integration)}
              onUnlink={() => {
                if (!link)
                  throw new Error(
                    'unreachable.  row.original.link should be defined in order to unlink',
                  )

                onUnlink(project, link)
              }}
            />
          )
        },
      },
    ),
  ] as ColumnDef<PMIRow>[]

  return (
    <TwoColumn
      label={t('common.projectIntegrations')}
      description={t('features.projectsAdmin.projectDetails.pmiDescription')}
    >
      <SimpleTable
        className="w-[825px]"
        columns={columns}
        data={integrationLinks}
        defaultSortColumn="integration"
      />
    </TwoColumn>
  )
}
